import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react'

import { AnimatePresence, motion, type HTMLMotionProps } from 'motion/react'
import { tv } from 'tailwind-variants'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import { Button, type ButtonProps } from '../../atoms/button'
import { Text } from '../../atoms/text'
import {
  TabsIndicator,
  TabsList,
  TabsContent,
  TabsTrigger,
  type TabsContentProps,
  type TabsListProps,
  type TabsTriggerProps
} from '../tabs'

const pageHeaderStyles = tv({
  slots: {
    root: 'bg-background w-full',
    topBar:
      'bg-surface border-outline flex min-h-16 flex-wrap items-center justify-between gap-3 border-b px-6 py-3',
    topBarStart: 'flex min-w-0 items-center',
    topBarEnd: 'flex items-center gap-4',
    content: 'flex w-full flex-col px-6 pb-4 pt-6',
    heading: 'flex flex-col gap-2',
    titleRow: 'flex flex-wrap items-center gap-2',
    titleGroup: 'flex min-w-0 items-center gap-3 pr-2',
    title: 'text-surface-title-100 tracking-normal lg:text-[2rem]/[2.5625rem]',
    actions: 'flex flex-wrap items-center gap-2',
    subtitle: 'text-surface-body',
    details: 'mt-4 flex flex-col items-start gap-4',
    tags: 'relative flex flex-wrap items-center gap-2',
    portalAddress: 'flex min-w-0 items-center',
    tabs: '-mb-0.5 mt-6 min-w-0 overflow-x-auto overflow-y-hidden pb-0.5'
  }
})

export interface PageHeaderProps
  extends Omit<ComponentPropsWithoutRef<'header'>, 'children' | 'title'> {
  /** A full-width announcement above the global bar, usually MarketingBanner. */
  banner?: ReactNode
  /** Left side of the global bar, usually Breadcrumb. */
  topBarStart?: ReactNode
  /** Right side of the global bar, such as the assistant action and Avatar. */
  topBarEnd?: ReactNode
  title: ReactNode
  titleAdornment?: ReactNode
  actions?: ReactNode
  subtitle?: ReactNode
  tags?: ReactNode
  portalAddress?: ReactNode
  /** Navigation below the page details, usually Acqua Tabs. */
  tabs?: ReactNode
}

export interface PageHeaderTopBarProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  start?: ReactNode
  end?: ReactNode
}

/** The global breadcrumb and account controls can sit outside a page's scroller. */
export const PageHeaderTopBar = forwardRef<
  HTMLDivElement,
  PageHeaderTopBarProps
>(({ start, end, className, ...props }, ref) => {
  const styles = pageHeaderStyles()

  return (
    <div
      ref={ref}
      data-slot='page-header-top-bar'
      className={cn(styles.topBar(), className)}
      {...props}
    >
      <div className={styles.topBarStart()}>{start}</div>
      {end && <div className={styles.topBarEnd()}>{end}</div>}
    </div>
  )
})
PageHeaderTopBar.displayName = 'PageHeaderTopBar'

/** The complete Ømni page header, with each optional Figma area as a slot. */
export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(
  (
    {
      banner,
      topBarStart,
      topBarEnd,
      title,
      titleAdornment,
      actions,
      subtitle,
      tags,
      portalAddress,
      tabs,
      className,
      ...props
    },
    ref
  ) => {
    const styles = pageHeaderStyles()
    const hasTopBar = topBarStart || topBarEnd
    const hasDetails = tags || portalAddress

    return (
      <header
        ref={ref}
        data-slot='page-header'
        className={cn(styles.root(), className)}
        {...props}
      >
        {banner}
        {hasTopBar && <PageHeaderTopBar start={topBarStart} end={topBarEnd} />}
        <div data-slot='page-header-content' className={styles.content()}>
          <div className={styles.heading()}>
            <div className={styles.titleRow()}>
              <div className={styles.titleGroup()}>
                <Text
                  as='h1'
                  variant='h4'
                  weight='medium'
                  className={styles.title()}
                >
                  {title}
                </Text>
                {titleAdornment}
              </div>
              {actions && (
                <div
                  data-slot='page-header-actions'
                  className={styles.actions()}
                >
                  {actions}
                </div>
              )}
            </div>
            {subtitle && (
              <Text as='p' variant='body-lg' className={styles.subtitle()}>
                {subtitle}
              </Text>
            )}
          </div>
          {hasDetails && (
            <div className={styles.details()}>
              {tags && (
                <div data-slot='page-header-tags' className={styles.tags()}>
                  {tags}
                </div>
              )}
              {portalAddress && (
                <div
                  data-slot='page-header-portal-address'
                  className={styles.portalAddress()}
                >
                  {portalAddress}
                </div>
              )}
            </div>
          )}
          {tabs && (
            <div data-slot='page-header-tabs' className={styles.tabs()}>
              {tabs}
            </div>
          )}
        </div>
      </header>
    )
  }
)
PageHeader.displayName = 'PageHeader'

export interface PageHeaderLayoutProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  header: ReactNode
  children: ReactNode
}

/** Maintains the 16px gap between the header and the page body. */
export const PageHeaderLayout = forwardRef<
  HTMLDivElement,
  PageHeaderLayoutProps
>(({ header, children, className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot='page-header-layout'
    className={cn('flex w-full flex-col gap-4', className)}
    {...props}
  >
    {header}
    <div data-slot='page-header-body' className='min-w-0'>
      {children}
    </div>
  </div>
))
PageHeaderLayout.displayName = 'PageHeaderLayout'

export interface PageHeaderTagsPresenceProps {
  children: ReactNode
}

/** Keeps keyed tags mounted while their exit animation completes. */
export const PageHeaderTagsPresence = ({
  children
}: PageHeaderTagsPresenceProps) => (
  <AnimatePresence initial={false} mode='popLayout'>
    {children}
  </AnimatePresence>
)

export type PageHeaderTagTransitionProps = Omit<
  HTMLMotionProps<'span'>,
  'layout' | 'initial' | 'exit' | 'transition'
>

/** Wraps an Acqua CustomerTag so sibling tags reflow after removal. */
export const PageHeaderTagTransition = forwardRef<
  HTMLSpanElement,
  PageHeaderTagTransitionProps
>(({ className, ...props }, ref) => {
  const reducedMotion = useReducedMotion()

  return (
    <motion.span
      ref={ref}
      data-slot='page-header-tag-transition'
      className={cn('inline-flex shrink-0', className)}
      layout={!reducedMotion}
      initial={false}
      exit={
        reducedMotion
          ? { opacity: 0, transition: { duration: 0 } }
          : { opacity: 0, scale: 0.94 }
      }
      transition={{ duration: 0.24, ease: 'easeOut' }}
      {...props}
    />
  )
})
PageHeaderTagTransition.displayName = 'PageHeaderTagTransition'

const assistantParticles = Array.from({ length: 20 }, (_, index) => {
  const angle = (index / 20) * Math.PI * 2
  const radius = 18 + (index % 4) * 10

  return {
    x: Math.round(Math.cos(angle) * radius),
    y: Math.round(Math.sin(angle) * radius),
    size: [2, 3, 4, 3, 5][index % 5],
    color: [
      'var(--color-on-primary)',
      'var(--page-header-assistant-particle-ice)',
      'var(--page-header-assistant-particle-lilac)',
      'var(--page-header-assistant-particle-blue)',
      'var(--page-header-assistant-particle-sky)'
    ][index % 5],
    delay: -index * 0.18
  }
})

export type PageHeaderAssistantButtonProps = Omit<
  ButtonProps,
  'children' | 'size' | 'variant'
> & {
  children?: ReactNode
}

/** The 126 × 32 Ask Alti action from the Ømni global bar. */
export const PageHeaderAssistantButton = forwardRef<
  HTMLButtonElement,
  PageHeaderAssistantButtonProps
>(({ children = 'Ask Alti', className, ...props }, ref) => (
  <Button
    ref={ref}
    size='sm'
    variant='primary'
    data-slot='page-header-assistant-button'
    contentClassName='gap-[9px]'
    className={cn(
      'text-control-lg relative isolate h-8 min-w-[126px] animate-[page-header-assistant-shift_9s_ease-in-out_infinite] gap-[9px] overflow-visible rounded-[9px] border-transparent bg-[image:var(--page-header-assistant-gradient)] bg-[length:200%_200%] px-4 text-white shadow-[var(--page-header-assistant-shadow)] motion-reduce:animate-none',
      className
    )}
    {...props}
  >
    <svg
      aria-hidden='true'
      width='21.081'
      height='18.286'
      viewBox='0 0 21.0807 18.2857'
      fill='none'
      className='shrink-0 animate-[page-header-assistant-sparkle_4s_ease-in-out_infinite] motion-reduce:animate-none'
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M12.3092 0.00864881V0.00588188L12.3067 0.00437021L12.3067 0.00864881H12.3092Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M12.4886 18.2814L12.4885 18.2842H12.4911V18.2814H12.4886Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M8.57454 18.2814V18.2842L8.57707 18.2857L8.57707 18.2814H8.57454Z'
        fill='currentColor'
      />
      <path
        d='M8.77406 0.00437021C7.58299 1.30602 8.23733 0.6221 7.58299 1.30602L0 9.21424L7.58295 17.1239C8.2394 17.8074 7.58295 17.1239 8.57707 18.2814H9.88474L9.89225 15.9857C9.89225 14.7614 9.33832 13.5597 8.2937 12.5092L5.01064 9.21424L8.29374 5.92066C9.34089 4.87016 9.89229 3.66851 9.89229 2.44418V0L8.77406 0.00437021Z'
        fill='currentColor'
      />
      <path
        d='M12.3067 0.00864881L11.1884 0.00437021V2.44261C11.1884 3.66694 11.7424 4.86859 12.787 5.91909L16.0701 9.21267L12.7869 12.5077C11.7398 13.5582 11.1884 14.7598 11.1884 15.9842L11.1809 18.2814H12.4886C13.4977 17.1223 12.8433 17.8062 13.4977 17.1223L21.0807 9.21267L13.4977 1.30445C12.8413 0.620969 13.4977 1.30445 12.3067 0.00864881Z'
        fill='currentColor'
      />
    </svg>
    {children}
    <span
      aria-hidden='true'
      className='pointer-events-none absolute left-3 top-1 size-1 animate-[page-header-assistant-sparkle_3.5s_ease-in-out_infinite] bg-white/90 [clip-path:polygon(50%_0%,62%_38%,100%_50%,62%_62%,50%_100%,38%_62%,0%_50%,38%_38%)] motion-reduce:animate-none'
    />
    <span
      aria-hidden='true'
      className='pointer-events-none absolute bottom-1 left-5 size-[3px] animate-[page-header-assistant-sparkle_4.5s_ease-in-out_infinite] bg-white/80 [clip-path:polygon(50%_0%,62%_38%,100%_50%,62%_62%,50%_100%,38%_62%,0%_50%,38%_38%)] motion-reduce:animate-none'
    />
    {assistantParticles.map((particle, index) => (
      <span
        key={index}
        aria-hidden='true'
        data-slot='page-header-assistant-particle'
        className='pointer-events-none absolute left-[calc(100%-8px)] top-1/2 opacity-0 [clip-path:polygon(50%_0%,62%_38%,100%_50%,62%_62%,50%_100%,38%_62%,0%_50%,38%_38%)] group-hover/button:animate-[page-header-assistant-particle_3.6s_linear_infinite] motion-reduce:group-hover/button:animate-none'
        style={
          {
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            animationDelay: `${particle.delay}s`,
            '--particle-x': `${particle.x}px`,
            '--particle-y': `${particle.y}px`
          } as CSSProperties
        }
      />
    ))}
  </Button>
))
PageHeaderAssistantButton.displayName = 'PageHeaderAssistantButton'

export type PageHeaderVerifiedBadgeProps = Omit<
  ComponentPropsWithoutRef<'svg'>,
  'children'
> & {
  label?: string
}

/** The 24px status mark in the title line; the 41px title line centers it. */
export const PageHeaderVerifiedBadge = forwardRef<
  SVGSVGElement,
  PageHeaderVerifiedBadgeProps
>(({ label = 'Verified', className, ...props }, ref) => (
  <svg
    ref={ref}
    role='img'
    aria-label={label}
    data-slot='page-header-verified-badge'
    viewBox='0 0 24 24'
    className={cn('size-6 shrink-0 self-center', className)}
    {...props}
  >
    <path
      d='M24 12C23.977 11.175 23.7254 10.3716 23.272 9.68071C22.8199 8.99106 22.1839 8.43933 21.4355 8.0894C21.7203 7.31417 21.7803 6.47509 21.6143 5.66666C21.447 4.85696 21.0562 4.11111 20.4879 3.51213C19.8876 2.9438 19.143 2.55427 18.3333 2.38569C17.5249 2.21966 16.6858 2.27969 15.9106 2.56449C15.5619 1.81481 15.0115 1.17752 14.3206 0.725411C13.6296 0.273304 12.8263 0.0204304 12 0C11.175 0.0217076 10.3742 0.272027 9.68454 0.725411C8.99489 1.1788 8.44699 1.81609 8.10089 2.56449C7.32439 2.27969 6.48275 2.21711 5.67177 2.38569C4.86079 2.55172 4.11366 2.94252 3.51341 3.51213C2.94508 4.11238 2.55683 4.85951 2.39208 5.66794C2.22605 6.47637 2.28991 7.31545 2.57599 8.0894C1.82631 8.43933 1.18774 8.98978 0.733074 9.67943C0.278413 10.3691 0.0242618 11.1737 0 12C0.025539 12.8263 0.278413 13.6296 0.733074 14.3206C1.18774 15.0102 1.82631 15.5619 2.57599 15.9106C2.28991 16.6845 2.22605 17.5236 2.39208 18.3321C2.55811 19.1418 2.94508 19.8876 3.51213 20.4879C4.11238 21.0536 4.85823 21.4419 5.66666 21.6092C6.47509 21.7778 7.31417 21.7165 8.0894 21.4355C8.43933 22.1839 8.98978 22.8199 9.68071 23.2733C10.3704 23.7254 11.175 23.977 12 24C12.8263 23.9796 13.6296 23.728 14.3206 23.2759C15.0115 22.8238 15.5619 22.1852 15.9106 21.4368C16.682 21.742 17.5275 21.8148 18.341 21.6462C19.1533 21.4776 19.8991 21.0753 20.4866 20.4879C21.0741 19.9004 21.4776 19.1545 21.6462 18.341C21.8148 17.5275 21.742 16.682 21.4355 15.9106C22.1839 15.5607 22.8199 15.0102 23.2733 14.3193C23.7254 13.6296 23.977 12.825 24 12ZM10.2912 16.917L5.91187 12.5389L7.56321 10.8761L10.2094 13.5223L15.8289 7.39974L17.5492 8.99106L10.2912 16.917Z'
      fill='var(--color-verified-badge)'
    />
  </svg>
))
PageHeaderVerifiedBadge.displayName = 'PageHeaderVerifiedBadge'

export interface PageHeaderCopyButtonProps
  extends Omit<
    ButtonProps,
    'children' | 'size' | 'variant' | 'iconOnly' | 'onClick'
  > {
  textToCopy: string
}

const COPY_FEEDBACK_MS = 1600

/** Acqua icon button sized to the portal link's 21px line height. */
export const PageHeaderCopyButton = forwardRef<
  HTMLButtonElement,
  PageHeaderCopyButtonProps
>(
  (
    { className, textToCopy, 'aria-label': ariaLabel = 'Copy link', ...props },
    ref
  ) => {
    const reducedMotion = useReducedMotion()
    const [feedback, setFeedback] = useState<'idle' | 'copied' | 'error'>(
      'idle'
    )
    const [animationCycle, setAnimationCycle] = useState(0)
    const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const latestCopy = useRef(0)

    useEffect(
      () => () => {
        latestCopy.current += 1
        if (resetTimer.current) clearTimeout(resetTimer.current)
      },
      []
    )

    const handleCopy = async () => {
      const request = ++latestCopy.current

      try {
        await navigator.clipboard.writeText(textToCopy)
        if (request !== latestCopy.current) return
        setAnimationCycle(cycle => cycle + 1)
        setFeedback('copied')
      } catch {
        if (request !== latestCopy.current) return
        setFeedback('error')
      }

      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(
        () => setFeedback('idle'),
        COPY_FEEDBACK_MS
      )
    }

    return (
      <Button
        ref={ref}
        variant='tertiary'
        size='sm'
        iconOnly
        aria-label={ariaLabel}
        data-slot='page-header-copy-button'
        className={cn('!size-[21px] !border-0 !p-0', className)}
        onClick={() => void handleCopy()}
        {...props}
      >
        <svg
          aria-hidden='true'
          viewBox='0 0 21 21'
          className={cn(
            'size-[21px] shrink-0 transition-colors duration-200 motion-reduce:transition-none',
            feedback === 'copied' ? 'text-success-icon' : 'text-link-fresh'
          )}
        >
          <AnimatePresence initial={false} mode='wait'>
            {feedback === 'copied' ? (
              <motion.g
                key={`copied-${animationCycle}`}
                style={{ transformOrigin: '50% 50%' }}
                initial={
                  reducedMotion ? false : { opacity: 0, scale: 0.8, y: 2 }
                }
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={reducedMotion ? undefined : { opacity: 0, scale: 0.9 }}
                transition={{ duration: reducedMotion ? 0 : 0.2 }}
              >
                <rect
                  x='4.5'
                  y='2.25'
                  width='12'
                  height='16.5'
                  rx='2.25'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.5'
                />
                <motion.path
                  d='m7.5 10.75 2.3 2.3 4-4.1'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.6'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  initial={reducedMotion ? false : { pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    duration: reducedMotion ? 0 : 0.3,
                    delay: reducedMotion ? 0 : 0.12
                  }}
                />
              </motion.g>
            ) : (
              <motion.path
                key='copy'
                opacity={1}
                d='M7.92553 15.3125C7.48351 15.3125 7.10937 15.1594 6.80312 14.8531C6.49687 14.5469 6.34375 14.1727 6.34375 13.7307V3.76928C6.34375 3.32726 6.49687 2.95312 6.80312 2.64687C7.10937 2.34062 7.48351 2.1875 7.92553 2.1875H15.262C15.704 2.1875 16.0781 2.34062 16.3844 2.64687C16.6906 2.95312 16.8437 3.32726 16.8437 3.76928V13.7307C16.8437 14.1727 16.6906 14.5469 16.3844 14.8531C16.0781 15.1594 15.704 15.3125 15.262 15.3125H7.92553ZM7.92553 14H15.262C15.3293 14 15.391 13.9719 15.447 13.9158C15.5032 13.8598 15.5312 13.7981 15.5312 13.7307V3.76928C15.5312 3.70191 15.5032 3.64022 15.447 3.58422C15.391 3.52807 15.3293 3.5 15.262 3.5H7.92553C7.85816 3.5 7.79647 3.52807 7.74047 3.58422C7.68432 3.64022 7.65625 3.70191 7.65625 3.76928V13.7307C7.65625 13.7981 7.68432 13.8598 7.74047 13.9158C7.79647 13.9719 7.85816 14 7.92553 14ZM4.86303 18.375C4.42101 18.375 4.04687 18.2219 3.74062 17.9156C3.43437 17.6094 3.28125 17.2352 3.28125 16.7932V6.17553C3.28125 5.9893 3.3441 5.83333 3.46981 5.70762C3.59537 5.58206 3.75127 5.51928 3.9375 5.51928C4.12373 5.51928 4.2797 5.58206 4.40541 5.70762C4.53097 5.83333 4.59375 5.9893 4.59375 6.17553V16.7932C4.59375 16.8606 4.62182 16.9223 4.67797 16.9783C4.73397 17.0344 4.79566 17.0625 4.86303 17.0625H12.8557C13.0419 17.0625 13.1979 17.1253 13.3236 17.2508C13.4492 17.3766 13.512 17.5325 13.512 17.7187C13.512 17.905 13.4492 18.0609 13.3236 18.1864C13.1979 18.3121 13.0419 18.375 12.8557 18.375H4.86303Z'
                fill='currentColor'
                initial={false}
                exit={reducedMotion ? undefined : { opacity: 0, y: -2 }}
                transition={{ duration: reducedMotion ? 0 : 0.16 }}
              />
            )}
          </AnimatePresence>
        </svg>
        <span role='status' className='sr-only'>
          {feedback === 'copied'
            ? 'Copied to clipboard'
            : feedback === 'error'
            ? 'Copy failed'
            : ''}
        </span>
      </Button>
    )
  }
)
PageHeaderCopyButton.displayName = 'PageHeaderCopyButton'

export type PageHeaderTabsListProps = Omit<TabsListProps, 'variant'>

/** Full-width line navigation measured from the Ømni page header. */
export const PageHeaderTabsList = forwardRef<
  HTMLDivElement,
  PageHeaderTabsListProps
>(({ children, className, ...props }, ref) => {
  const reducedMotion = useReducedMotion()

  return (
    <TabsList
      ref={ref}
      variant='page-header'
      data-slot='page-header-tabs-list'
      className={className}
      {...props}
    >
      {children}
      <TabsIndicator
        data-slot='page-header-tabs-indicator'
        className='bg-primary absolute -bottom-0.5 h-1'
        style={{
          left: 'var(--active-tab-left)',
          width: 'calc(var(--active-tab-width) + 4px)',
          borderRadius: 9999
        }}
        render={
          <motion.span
            layout={!reducedMotion}
            initial={false}
            transition={{ type: 'spring', stiffness: 500, damping: 40 }}
          />
        }
      />
    </TabsList>
  )
})
PageHeaderTabsList.displayName = 'PageHeaderTabsList'

export type PageHeaderTabsViewportProps = ComponentPropsWithoutRef<'div'>

/** Clips the two moving Base UI panels to the page content area. */
export const PageHeaderTabsViewport = forwardRef<
  HTMLDivElement,
  PageHeaderTabsViewportProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot='page-header-tabs-viewport'
    className={cn(
      'grid w-full overflow-hidden [&>*]:col-start-1 [&>*]:row-start-1',
      className
    )}
    {...props}
  />
))
PageHeaderTabsViewport.displayName = 'PageHeaderTabsViewport'

export type PageHeaderTabsPanelProps = Omit<TabsContentProps, 'render'>

/** Directional Motion slide and fade while Base UI owns panel accessibility. */
export const PageHeaderTabsPanel = forwardRef<
  HTMLDivElement,
  PageHeaderTabsPanelProps
>(({ className, ...props }, ref) => {
  const reducedMotion = useReducedMotion()

  return (
    <TabsContent
      ref={ref}
      data-slot='page-header-tabs-panel'
      className={cn(
        'mt-0 min-w-0 transition-opacity duration-[350ms] ease-out',
        'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
        'motion-reduce:transition-none',
        className
      )}
      render={(panelProps, state) => {
        const offset =
          state.tabActivationDirection === 'right'
            ? 64
            : state.tabActivationDirection === 'left'
            ? -64
            : 0
        const x = reducedMotion
          ? 0
          : state.transitionStatus === 'starting'
          ? offset
          : state.transitionStatus === 'ending'
          ? -offset
          : 0

        return (
          <motion.div
            {...(panelProps as HTMLMotionProps<'div'>)}
            initial={false}
            animate={{ x }}
            transition={{ duration: reducedMotion ? 0 : 0.35, ease: 'easeOut' }}
          />
        )
      }}
      {...props}
    />
  )
})
PageHeaderTabsPanel.displayName = 'PageHeaderTabsPanel'

export type PageHeaderTabProps = TabsTriggerProps

export const PageHeaderTab = forwardRef<HTMLElement, PageHeaderTabProps>(
  ({ children, className, ...props }, ref) => (
    <TabsTrigger
      ref={ref}
      data-slot='page-header-tab'
      className={cn('shrink-0', className)}
      {...props}
    >
      {typeof children === 'string' || typeof children === 'number' ? (
        <span className='grid'>
          <span
            aria-hidden='true'
            className='invisible col-start-1 row-start-1 font-medium'
          >
            {children}
          </span>
          <span className='col-start-1 row-start-1'>{children}</span>
        </span>
      ) : (
        children
      )}
    </TabsTrigger>
  )
)
PageHeaderTab.displayName = 'PageHeaderTab'
